/**
 * Subscription Plans Page
 * Dynamic pricing page that fetches tiers from the backend API
 * Uses PARACLETE dark theme design tokens
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Check, Zap, Shield, Crown, Calendar, CreditCard, Users, Loader2, AlertCircle, X, ExternalLink } from 'lucide-react';
import { useBillingStore } from '../stores/billingStore';
import { toast } from 'react-hot-toast';
import SubscriptionStatus from '../components/subscription/SubscriptionStatus';
import Modal from '../components/common/Modal';
import {
  getPublicPlans,
  getCurrentSubscription,
  PublicTier,
  PublicPlansResponse,
  CurrentSubscriptionResponse,
} from '../api/subscriptions';

// ChargeKeep Payment Links Configuration
// Maps tier_code + billing_cycle to ChargeKeep checkout URLs and invoice IDs
interface ChargeKeepPaymentConfig {
  url: string;
  invoiceId: string;
}

const CHARGEKEEP_PAYMENT_LINKS: Record<string, Record<'monthly' | 'yearly', ChargeKeepPaymentConfig | null>> = {
  // Individual Plans
  basic: {
    monthly: { url: 'https://app.chargekeep.com/i/f81e8b38d55b6930', invoiceId: '60576' },
    yearly: { url: 'https://app.chargekeep.com/i/5f2045da4da96fae', invoiceId: '60577' },
  },
  advanced: {
    monthly: { url: 'https://app.chargekeep.com/i/9146815c3f7fa019', invoiceId: '60580' },
    yearly: { url: 'https://app.chargekeep.com/i/1b2174dc1e8066ae', invoiceId: '60578' },
  },
  pro: {
    monthly: { url: 'https://app.chargekeep.com/i/a39d3a3aa058f7ea', invoiceId: '60581' },
    yearly: { url: 'https://app.chargekeep.com/i/f8c4a952865547a9', invoiceId: '60579' },
  },
  // Organization Plans
  org_5: {
    monthly: { url: 'https://app.chargekeep.com/i/f1849b4911244ac2', invoiceId: '60582' },
    yearly: { url: 'https://app.chargekeep.com/i/1583ff189d88b64c', invoiceId: '60585' },
  },
  org_10: {
    monthly: { url: 'https://app.chargekeep.com/i/acad6708f2ada48f', invoiceId: '60583' },
    yearly: { url: 'https://app.chargekeep.com/i/b74bbef58111837b', invoiceId: '60586' },
  },
  org_25: {
    monthly: { url: 'https://app.chargekeep.com/i/f143e31d070cd120', invoiceId: '60584' },
    yearly: { url: 'https://app.chargekeep.com/i/d02191ada6c21ac7', invoiceId: '60587' },
  },
};

// Helper to open ChargeKeep payment popup
const openChargeKeepPopup = (config: ChargeKeepPaymentConfig): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Create a hidden anchor element with the ChargeKeep payment link
    const anchor = document.createElement('a');
    anchor.href = config.url;
    anchor.className = 'chargekeep-payment-form-btn';
    anchor.id = `chargekeep-payment-${config.invoiceId}`;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);

    // Load the embed form script for this specific invoice
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://app.chargekeep.com/invoices/${config.invoiceId}/embed_form_script.js`;

    script.onload = () => {
      // Give the script a moment to initialize, then trigger click
      setTimeout(() => {
        anchor.click();
        resolve();
      }, 100);
    };

    script.onerror = () => {
      document.body.removeChild(anchor);
      reject(new Error('Failed to load ChargeKeep payment form'));
    };

    document.body.appendChild(script);

    // Cleanup after a delay (the modal will stay open)
    setTimeout(() => {
      if (anchor.parentNode) document.body.removeChild(anchor);
      if (script.parentNode) document.body.removeChild(script);
    }, 2000);
  });
};

// Icon mapping for tiers
const getTierIcon = (tierCode: string): React.ReactNode => {
  const iconClass = "w-8 h-8";
  if (tierCode.includes('free') || tierCode.includes('basic')) {
    return <Shield className={iconClass} />;
  }
  if (tierCode.includes('pro') || tierCode.includes('advanced')) {
    return <Zap className={iconClass} />;
  }
  if (tierCode.includes('org') || tierCode.includes('enterprise')) {
    return <Crown className={iconClass} />;
  }
  return <Shield className={iconClass} />;
};

// Feature labels for display
const FEATURE_LABELS: Record<string, string> = {
  file_upload: 'File uploads',
  api_access: 'API access',
  custom_prompts: 'Custom prompts',
  advanced_search: 'Advanced search',
  priority_support: 'Priority support',
  bulk_operations: 'Bulk operations',
  team_management: 'Team management',
  custom_integrations: 'Custom integrations',
};

// Confirmation Modal Component
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onChargeKeepCheckout: () => void;
  tier: PublicTier | null;
  billingCycle: 'monthly' | 'yearly';
  isLoading: boolean;
  hasChargeKeepLink: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onChargeKeepCheckout,
  tier,
  billingCycle,
  isLoading,
  hasChargeKeepLink,
}) => {
  if (!tier) return null;

  const price = billingCycle === 'monthly' ? tier.price_monthly : tier.price_yearly;
  const periodLabel = billingCycle === 'monthly' ? '/month' : '/year';

  // Use ChargeKeep checkout if available, otherwise fall back to API
  const handleCheckout = hasChargeKeepLink ? onChargeKeepCheckout : onConfirm;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-md">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-dark-text">Confirm Subscription</h2>
          <button
            onClick={onClose}
            className="p-2 text-dark-text-muted hover:text-dark-text rounded-lg hover:bg-dark-border/50 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Plan Summary */}
        <div className="bg-dark-bg/50 rounded-lg p-4 mb-6 border border-dark-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-accent-blue">
              {getTierIcon(tier.tier_code)}
            </div>
            <div>
              <h3 className="font-semibold text-dark-text">{tier.display_name}</h3>
              <p className="text-sm text-dark-text-muted">{tier.description}</p>
            </div>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-dark-text">
              ${price?.toFixed(2)}
            </span>
            <span className="text-dark-text-muted">{periodLabel}</span>
          </div>
        </div>

        {/* Confirmation Text */}
        <p className="text-dark-text-muted text-sm mb-6">
          You are about to subscribe to the <strong className="text-dark-text">{tier.display_name}</strong> plan.
          {tier.trial_days > 0 && (
            <span className="block mt-2 text-accent-blue">
              Includes {tier.trial_days}-day free trial!
            </span>
          )}
        </p>

        {/* Secure Payment Notice */}
        {hasChargeKeepLink && (
          <div className="flex items-center gap-2 text-xs text-dark-text-muted mb-4 bg-dark-bg/30 p-3 rounded-lg">
            <Shield className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>Secure checkout powered by ChargeKeep. Your payment details are encrypted.</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-lg font-medium text-dark-text bg-dark-border hover:bg-dark-border/70 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCheckout}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-accent-blue to-accent-orange hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {hasChargeKeepLink && <CreditCard className="w-4 h-4" />}
                Proceed to Payment
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const SubscriptionPlans: React.FC = () => {
  const { createNewSubscription, isCreating, syncProfile } = useBillingStore();

  // State
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PublicPlansResponse | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscriptionResponse | null>(null);

  // Confirmation modal state
  const [confirmationTier, setConfirmationTier] = useState<PublicTier | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch plans and current subscription on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Sync customer profile first
        await syncProfile().catch((err) => {
          console.error('Failed to sync customer profile:', err);
        });

        // Fetch plans and current subscription in parallel
        const [plansData, subscriptionData] = await Promise.all([
          getPublicPlans(),
          getCurrentSubscription().catch(() => null), // Might fail if not logged in
        ]);

        setPlans(plansData);
        setCurrentSubscription(subscriptionData);
      } catch (err: any) {
        console.error('Failed to fetch subscription data:', err);
        // Handle both string and object error formats
        const detail = err.response?.data?.detail;
        let errorMessage = 'Failed to load subscription plans';
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail?.message) {
          errorMessage = detail.message;
        }
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [syncProfile]);

  const formatPrice = (amount: number | null): string => {
    if (amount === null || amount === 0) return 'Free';
    return `$${amount.toFixed(2)}`;
  };

  const getFeaturesList = (tier: PublicTier): string[] => {
    const features: string[] = [];

    // Add limits as features
    features.push(`${tier.limits.clipboard_slots} clipboard items`);
    features.push(`${tier.limits.conversations_limit} conversations`);
    features.push(`${tier.limits.ai_requests_limit} AI requests/month`);

    // Add feature flags
    Object.entries(tier.features).forEach(([key, enabled]) => {
      if (enabled && FEATURE_LABELS[key]) {
        features.push(FEATURE_LABELS[key]);
      }
    });

    // Add subuser info for org tiers
    if (tier.max_subusers) {
      features.push(`Up to ${tier.max_subusers} team members`);
    }

    return features;
  };

  const handleSelectPlan = (tier: PublicTier) => {
    const isFree = (tier.price_monthly === null || tier.price_monthly === 0) &&
                   (tier.price_yearly === null || tier.price_yearly === 0);

    if (isFree) {
      toast.error('You are already on the free tier');
      return;
    }

    // Open confirmation modal
    setConfirmationTier(tier);
    setShowConfirmation(true);
  };

  const handleConfirmSubscription = async () => {
    if (!confirmationTier) return;

    setSelectedPlan(confirmationTier.tier_code);

    try {
      const isRecurring = true; // Always recurring now (monthly or yearly)
      const response = await createNewSubscription(confirmationTier.tier_code, isRecurring);

      toast.success(response.message || 'Subscription created successfully!');
      setShowConfirmation(false);

      // Redirect to payment page if URL is provided
      if (response.payment_url) {
        try {
          const url = new URL(response.payment_url);
          const allowedHosts = ['chargekeep.com', 'crm.chargekeep.com'];

          if (allowedHosts.some(host => url.hostname === host || url.hostname.endsWith(`.${host}`))) {
            window.location.href = response.payment_url;
          } else {
            console.error('Invalid payment URL hostname:', url.hostname);
            toast.error('Invalid payment URL received. Please contact support.');
          }
        } catch (urlError) {
          console.error('Invalid payment URL format:', response.payment_url);
          toast.error('Invalid payment URL format. Please contact support.');
        }
      }
    } catch (err: any) {
      console.error('Subscription creation failed:', err);
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to create subscription';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.message) {
        errorMessage = detail.message;
      }
      toast.error(errorMessage);
      setSelectedPlan(null);
    }
  };

  // Get ChargeKeep payment config for the selected tier and billing cycle
  const getChargeKeepConfig = useCallback((tierCode: string, cycle: 'monthly' | 'yearly'): ChargeKeepPaymentConfig | null => {
    const tierConfig = CHARGEKEEP_PAYMENT_LINKS[tierCode];
    return tierConfig?.[cycle] || null;
  }, []);

  // Handle ChargeKeep popup checkout
  const handleChargeKeepCheckout = useCallback(async () => {
    if (!confirmationTier) return;

    const config = getChargeKeepConfig(confirmationTier.tier_code, billingCycle);
    if (!config) {
      // No ChargeKeep link available, fall back to API
      handleConfirmSubscription();
      return;
    }

    setSelectedPlan(confirmationTier.tier_code);

    try {
      // Open ChargeKeep popup
      await openChargeKeepPopup(config);
      setShowConfirmation(false);
      toast.success('Payment form opened. Complete your payment in the popup.');
    } catch (err: any) {
      console.error('ChargeKeep checkout failed:', err);
      toast.error('Failed to open payment form. Please try again.');
      setSelectedPlan(null);
    }
  }, [confirmationTier, billingCycle, getChargeKeepConfig]);

  // Check if ChargeKeep link is available for the selected tier
  const hasChargeKeepLink = confirmationTier
    ? !!getChargeKeepConfig(confirmationTier.tier_code, billingCycle)
    : false;

  const renderPlanCard = (tier: PublicTier, isPopular: boolean = false) => {
    const isCurrentPlan = currentSubscription?.current_tier.tier_code === tier.tier_code;
    const price = billingCycle === 'monthly' ? tier.price_monthly : tier.price_yearly;
    const isFree = (tier.price_monthly === null || tier.price_monthly === 0);
    const isLoadingThis = selectedPlan === tier.tier_code && isCreating;
    const features = getFeaturesList(tier);

    return (
      <div
        key={tier.tier_code}
        className={`
          relative bg-dark-card/95 backdrop-blur-xl rounded-xl shadow-2xl border transition-all duration-200
          ${isPopular ? 'border-accent-orange shadow-accent-orange/20 scale-105' : 'border-dark-border'}
          ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}
          hover:shadow-xl hover:border-accent-blue/50
        `}
        role="article"
        aria-label={`${tier.display_name} subscription plan`}
      >
        {/* Popular Badge */}
        {isPopular && (
          <div className="absolute top-0 right-0 bg-gradient-to-r from-accent-blue to-accent-orange text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-xs font-semibold">
            MOST POPULAR
          </div>
        )}

        {/* Current Plan Badge */}
        {isCurrentPlan && (
          <div className="absolute top-0 left-0 bg-green-600 text-white px-4 py-1 rounded-br-lg rounded-tl-xl text-xs font-semibold">
            CURRENT PLAN
          </div>
        )}

        <div className="p-8">
          {/* Icon & Name */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`${isPopular ? 'text-accent-orange' : 'text-accent-blue'}`}>
              {getTierIcon(tier.tier_code)}
            </div>
            <h3 className="text-2xl font-bold text-dark-text">{tier.display_name}</h3>
          </div>

          {/* Description */}
          <p className="text-dark-text-muted mb-6 min-h-[3rem]">{tier.description || ''}</p>

          {/* Price */}
          <div className="mb-6">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-dark-text">
                {formatPrice(price)}
              </span>
              {!isFree && (
                <span className="text-dark-text-muted">
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </span>
              )}
            </div>
            {!isFree && billingCycle === 'yearly' && tier.price_monthly && tier.price_yearly && (
              <p className="text-sm text-green-500 mt-1">
                Save ${((tier.price_monthly * 12) - tier.price_yearly).toFixed(2)}/year
              </p>
            )}
            {tier.trial_days > 0 && (
              <p className="text-sm text-accent-blue mt-1">
                {tier.trial_days}-day free trial
              </p>
            )}
          </div>

          {/* Features */}
          <ul className="space-y-3 mb-8" role="list" aria-label={`${tier.display_name} features`}>
            {features.slice(0, 8).map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-dark-text-muted text-sm">{feature}</span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <button
            onClick={() => handleSelectPlan(tier)}
            disabled={isFree || isCurrentPlan || isLoadingThis}
            aria-label={isFree ? 'Free tier - no action required' : isCurrentPlan ? 'This is your current plan' : `Subscribe to ${tier.display_name}`}
            className={`
              w-full py-3 px-6 rounded-lg font-semibold text-base
              transition-all duration-200 transform
              focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 focus:ring-offset-dark-card
              ${isPopular
                ? 'bg-gradient-to-r from-accent-blue to-accent-orange text-white hover:opacity-90 hover:scale-105 shadow-lg'
                : 'bg-accent-blue text-white hover:bg-accent-blue/90 hover:scale-105'
              }
              ${(isFree || isCurrentPlan) && 'opacity-50 cursor-not-allowed'}
              ${isLoadingThis && 'opacity-75 cursor-wait'}
              disabled:hover:scale-100
            `}
          >
            {isLoadingThis ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Processing...
              </span>
            ) : isCurrentPlan ? (
              'Current Plan'
            ) : isFree ? (
              'Free Tier'
            ) : (
              `Get ${tier.display_name}`
            )}
          </button>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center" role="status" aria-label="Loading subscription plans">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-accent-blue mx-auto mb-4" aria-hidden="true" />
          <p className="text-dark-text-muted">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center" role="alert">
        <div className="text-center max-w-md mx-auto p-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-xl font-bold text-dark-text mb-2">Failed to Load Plans</h2>
          <p className="text-dark-text-muted mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-accent-blue text-white px-6 py-2 rounded-lg hover:bg-accent-blue/90 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 focus:ring-offset-dark-bg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const hasIndividualPlans = plans && plans.individual_plans.length > 0;
  const hasOrgPlans = plans && plans.organization_plans.length > 0;

  return (
    <div className="min-h-screen bg-dark-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-dark-text mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-dark-text-muted max-w-2xl mx-auto">
            Select the subscription that best fits your research needs. All plans include
            7-day grace period protection.
          </p>
        </header>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8" role="group" aria-label="Billing cycle selection">
          <div className="bg-dark-card border border-dark-border rounded-lg p-1 inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              aria-pressed={billingCycle === 'monthly'}
              className={`
                px-6 py-2 rounded-md font-medium text-sm transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-inset
                ${billingCycle === 'monthly'
                  ? 'bg-accent-blue text-white shadow-sm'
                  : 'text-dark-text-muted hover:text-dark-text'
                }
              `}
            >
              <Calendar className="w-4 h-4 inline mr-2" aria-hidden="true" />
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              aria-pressed={billingCycle === 'yearly'}
              className={`
                px-6 py-2 rounded-md font-medium text-sm transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-inset
                ${billingCycle === 'yearly'
                  ? 'bg-accent-blue text-white shadow-sm'
                  : 'text-dark-text-muted hover:text-dark-text'
                }
              `}
            >
              <CreditCard className="w-4 h-4 inline mr-2" aria-hidden="true" />
              Yearly
            </button>
          </div>
        </div>

        {/* Individual Plans Section */}
        {hasIndividualPlans && (
          <section aria-labelledby="individual-plans-heading">
            <h2 id="individual-plans-heading" className="text-2xl font-bold text-dark-text mb-6 text-center">
              Individual Plans
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
              {plans!.individual_plans.map((tier, index) =>
                renderPlanCard(tier, index === 1) // Middle plan is "popular"
              )}
            </div>
          </section>
        )}

        {/* Organization Plans Section */}
        {hasOrgPlans && (
          <section aria-labelledby="org-plans-heading">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users className="w-6 h-6 text-accent-orange" aria-hidden="true" />
                <h2 id="org-plans-heading" className="text-2xl font-bold text-dark-text">
                  Organization Plans
                </h2>
              </div>
              <p className="text-dark-text-muted">
                Perfect for churches, ministries, and teams
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
              {plans!.organization_plans.map((tier, index) =>
                renderPlanCard(tier, index === 1)
              )}
            </div>
          </section>
        )}

        {/* No Plans Available */}
        {!hasIndividualPlans && !hasOrgPlans && (
          <div className="text-center py-12" role="status">
            <AlertCircle className="w-12 h-12 text-dark-text-muted mx-auto mb-4" aria-hidden="true" />
            <p className="text-dark-text-muted">No subscription plans are currently available.</p>
          </div>
        )}

        {/* FAQ / Info Section */}
        <aside className="mt-8 max-w-3xl mx-auto bg-dark-card/50 border border-dark-border rounded-xl p-8 backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-dark-text mb-4">
            Why Choose PARACLETE?
          </h2>
          <div className="space-y-4 text-dark-text-muted">
            <div>
              <h3 className="font-semibold mb-1 text-dark-text">Grace Period Protection</h3>
              <p className="text-sm">
                If a payment fails, you get 7 days of read-only access to export your data
                and update your payment method.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1 text-dark-text">Flexible Billing</h3>
              <p className="text-sm">
                Choose monthly or yearly billing. Yearly subscriptions save you 20%!
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1 text-dark-text">Built for Christian Research</h3>
              <p className="text-sm">
                Specialized AI models trained on theological content, doctrinal presets,
                and tools designed for ministry work.
              </p>
            </div>
          </div>
        </aside>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSubscription}
        onChargeKeepCheckout={handleChargeKeepCheckout}
        tier={confirmationTier}
        billingCycle={billingCycle}
        isLoading={isCreating}
        hasChargeKeepLink={hasChargeKeepLink}
      />
    </div>
  );
};

export default SubscriptionPlans;
